import { useAutoAnimate } from '@formkit/auto-animate/react';
import {
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  PencilSquareIcon,
  ScissorsIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import Tooltip from 'components/layout/tooltip';
import CropImageModal from 'components/structure-page/modals/crop-image-modal';
import EditSatelliteImageModal from 'components/structure-page/modals/edit-satellite-image-modal';
import { ProjectAttachmentType } from 'data/types';
import { deleteStructureImage } from 'features/data';
import { JSXElementConstructor, SVGProps, useState } from 'react';
import { useDrag } from 'react-dnd';
import axios from 'utils/api';
import {
  getAttachmentsByType,
  LAYOUT_IMAGE_TYPE,
  StructureImageTypeCode
} from 'utils/attachments';
import { loadStructureAttachments } from 'utils/data';
import { SATELLITE_IMAGE_NAME } from 'utils/satellite-image';
import { confirmationModal, errorModal } from 'utils/ui';

type DraggableImageCardProps = Readonly<{
  id: number;
  index: number;
  name: string;
  url: string;
  rotatingImageId: number | null;
  isSatellite?: boolean;
  onRotate: (id: number, direction: 'left' | 'right') => void;
  onCrop: (id: number, url: string, name: string) => void;
  onDelete: (id: number) => void;
  onEdit?: (id: number) => void;
}>;

function DraggableImageCard({
  id,
  index,
  name,
  url,
  rotatingImageId,
  isSatellite = false,
  onRotate,
  onCrop,
  onDelete,
  onEdit
}: DraggableImageCardProps) {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'structure-image',
      item: { id, name, type: 'structure-image' },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging()
      })
    }),
    [id]
  );

  return (
    <div
      ref={drag}
      id={`structure-image-${index}`}
      className={clsx(
        'flex w-full cursor-grab flex-col items-center gap-1 rounded-md border-2 border-gray-300 bg-gray-50 p-2',
        isDragging ? 'cursor-grabbing opacity-50' : ''
      )}
    >
      <div className='flex items-center gap-1 pb-1'>
        <button
          className='relative cursor-pointer text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50'
          type='button'
          disabled={rotatingImageId === id}
          onClick={(e) => {
            e.stopPropagation();
            onRotate(id, 'left');
          }}
        >
          <ArrowUturnLeftIcon className='h-4 w-4' />
          <Tooltip text='Rotate Left' />
        </button>
        <button
          className='relative cursor-pointer text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50'
          type='button'
          disabled={rotatingImageId === id}
          onClick={(e) => {
            e.stopPropagation();
            onRotate(id, 'right');
          }}
        >
          <ArrowUturnRightIcon className='h-4 w-4' />
          <Tooltip text='Rotate Right' />
        </button>
        {isSatellite && onEdit && (
          <button
            className='relative cursor-pointer text-gray-500 hover:text-gray-700'
            type='button'
            onClick={(e) => {
              e.stopPropagation();
              onEdit(id);
            }}
          >
            <PencilSquareIcon className='h-4 w-4' />
            <Tooltip text='Edit location, zoom & rotation' />
          </button>
        )}
        {!name.includes('(Cropped)') && (
          <button
            className='relative cursor-pointer text-gray-500 hover:text-gray-700'
            type='button'
            onClick={(e) => {
              e.stopPropagation();
              onCrop(id, url, name);
            }}
          >
            <ScissorsIcon className='h-4 w-4' />
            <Tooltip text='Crop Image' />
          </button>
        )}
        <button
          className='relative cursor-pointer text-gray-500 hover:text-gray-700'
          type='button'
          onClick={(e) => {
            e.stopPropagation();
            onDelete(id);
          }}
        >
          <TrashIcon className='h-4 w-4' />
          <Tooltip text='Delete Image' />
        </button>
      </div>
      <img
        src={url}
        alt={name}
        className='max-h-32 w-full rounded object-contain'
        draggable={false}
      />
      <span className='w-full truncate text-center text-xs'>{name}</span>
    </div>
  );
}

type UploadedImagesGridProps = {
  imageType?: StructureImageTypeCode;
  singleColumn?: boolean;
  title?: string;
  titleIcon?: JSXElementConstructor<SVGProps<SVGSVGElement>>;
};

export default function UploadedImagesGrid({
  imageType = LAYOUT_IMAGE_TYPE,
  singleColumn = false,
  title,
  titleIcon: TitleIcon
}: UploadedImagesGridProps) {
  const dispatch = useAppDispatch();
  const {
    structureAttachments,
    structureUniqueId,
    structureId,
    structureImageAssignments
  } = useAppSelector((state) => state.structure);
  const visibleAttachments = getAttachmentsByType(
    structureAttachments,
    imageType
  );
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<{
    id: number;
    url: string;
    name: string;
  } | null>(null);
  const [rotatingImageId, setRotatingImageId] = useState<number | null>(null);
  const [editSatelliteOpen, setEditSatelliteOpen] = useState(false);
  const [satelliteAttachment, setSatelliteAttachment] =
    useState<ProjectAttachmentType | null>(null);
  const [animate] = useAutoAnimate({ duration: 100 });

  const handleOpenCropModal = (
    attachmentId: number,
    attachmentUrl: string,
    attachmentName: string
  ) => {
    setSelectedAttachment({
      id: attachmentId,
      url: attachmentUrl,
      name: attachmentName
    });
    setCropModalOpen(true);
  };

  const handleEditSatellite = (attachmentId: number) => {
    const target =
      visibleAttachments.find((a) => a.id === attachmentId) ?? null;
    setSatelliteAttachment(target);
    setEditSatelliteOpen(true);
  };

  const handleDelete = (attachmentId: number) => {
    const isAssigned =
      !!structureImageAssignments[attachmentId]?.structureItemUniqueId;

    if (isAssigned) {
      confirmationModal(
        'This image is assigned to a level. Are you sure you want to delete it?',
        () => dispatch(deleteStructureImage(attachmentId))
      );
    } else {
      dispatch(deleteStructureImage(attachmentId));
    }
  };

  const handleRotateImage = async (
    attachmentId: number,
    direction: 'left' | 'right'
  ) => {
    if (!structureUniqueId || !structureId) {
      errorModal('No structure ID found');
      return;
    }

    setRotatingImageId(attachmentId);
    try {
      await axios.post('structure/rotate-structure-image', {
        attachmentId,
        structureUniqueId,
        direction
      });
      await loadStructureAttachments(structureId);
    } catch (error) {
      console.error('Error rotating image:', error);
      errorModal('Failed to rotate image');
    } finally {
      setRotatingImageId(null);
    }
  };

  if (visibleAttachments.length === 0) {
    return null;
  }

  return (
    <>
      {title && (
        <div className='mb-2 flex items-center gap-1'>
          {TitleIcon && <TitleIcon className='h-6 w-6' />}
          <span className='text-lg font-semibold text-gray-800'>{title}</span>
        </div>
      )}
      <div
        ref={animate}
        className={clsx(
          'grid w-full gap-3',
          singleColumn ? 'grid-cols-1' : 'grid-cols-2 sm:grid-cols-3'
        )}
      >
        {visibleAttachments.map((attachment, attachmentIndex) => (
          <DraggableImageCard
            key={attachment.id}
            id={attachment.id}
            index={attachmentIndex}
            name={attachment.name}
            url={attachment.url ?? ''}
            rotatingImageId={rotatingImageId}
            isSatellite={attachment.name === SATELLITE_IMAGE_NAME}
            onRotate={handleRotateImage}
            onCrop={handleOpenCropModal}
            onDelete={handleDelete}
            onEdit={handleEditSatellite}
          />
        ))}
      </div>

      <CropImageModal
        openState={[cropModalOpen, setCropModalOpen]}
        attachmentId={selectedAttachment?.id ?? null}
        attachmentUrl={selectedAttachment?.url ?? null}
        attachmentName={selectedAttachment?.name ?? null}
      />

      <EditSatelliteImageModal
        openState={[editSatelliteOpen, setEditSatelliteOpen]}
        attachment={satelliteAttachment}
      />
    </>
  );
}
